import { useApiStore } from '@/context/global/stores/apiStore';
import { useKaraokeStore } from '@/context/global/stores/karaokeStore';
import { getOrCreateDeviceId } from '@/features/auth/utils/deviceId';
import { isAxiosError } from 'axios';
import { useEffect, useRef, useCallback, useState } from 'react';

type UseSessionHeartbeatOptions = {
  intervalSeconds?: number;
  enabled?: boolean;
};

export const useSessionHeartbeat = ({
  intervalSeconds = 30,
  enabled = true,
}: UseSessionHeartbeatOptions) => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const tickCountRef = useRef(0);
  const { API } = useApiStore();
  const { sessionId } = useKaraokeStore();
  const [concurrentSessionError, setConcurrentSessionError] = useState(false);

  const deviceId = getOrCreateDeviceId();

  const clearConcurrentSessionError = useCallback(() => {
    setConcurrentSessionError(false);
  }, []);

  const handleTick = useCallback(async () => {
    if (!sessionId) return;

    tickCountRef.current += 1;

    if (process.env.NODE_ENV === 'development') console.log('[Session] - Session Sent');

    try {
      await API.Song.PostSongConcurrentSessionHeartBeat(sessionId, deviceId);
    } catch (error) {
      if (isAxiosError(error) && error.response?.data?.code === 603) {
        setConcurrentSessionError(true);
      }
    }
  }, [API.Song, deviceId, sessionId]);

  useEffect(() => {
    if (enabled) {
      // Run immediately on mount
      handleTick();
      // Then run every intervalSeconds
      intervalRef.current = setInterval(handleTick, intervalSeconds * 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [enabled, intervalSeconds, handleTick]);

  return {
    tickCount: tickCountRef.current,
    concurrentSessionError,
    clearConcurrentSessionError,
  };
};
